/**
 * Base Coupon Strategy
 * Abstract base class for all coupon type strategies
 *
 * Currently used methods:
 * - calculateDiscount() - Used by couponStrategyFactory for discount calculation
 * - getInitialResult() - Helper for default discount result
 *
 * Reserved for future use (not yet integrated):
 * - convertApiResultToState() - For API result conversion (currently in result-to-state.js)
 * - validate() - For coupon validation
 * - canApply() - For eligibility checking
 * - getType() - For type identification
 *
 * @typedef {import('@/types/Base').CouponState} CouponState
 * @typedef {import('@/types/Base').PlanItemBean} PlanItemBean
 * @typedef {import('@/types/Base').SingleAmountPlansMapBean} SingleAmountPlansMapBean
 * @typedef {import('@/types/Base').CouponDiscountMapResultBean} CouponDiscountMapResultBean
 */

/**
 * Abstract base class for coupon strategies
 * Each coupon type (TIMEBASE, MARKETING, ACCOUNT, etc.) should extend this class
 */
export class BaseCouponStrategy {
  /**
   * Calculate discount for selected plans
   * @abstract
   * @param {CouponState} couponState - Current coupon state
   * @param {Array<PlanItemBean>} selectedPlans - Selected plans array
   * @param {SingleAmountPlansMapBean} planWithAmountMap - Plan amount mapping
   * @param {Object} context - Additional context (store getters, etc.)
   * @returns {CouponDiscountMapResultBean} Calculated discount result
   */
  // eslint-disable-next-line no-unused-vars
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, context) {
    throw new Error('calculateDiscount() must be implemented by subclass');
  }

  /**
   * Validate coupon code
   * @abstract
   * @param {string} couponCode - Coupon code to validate
   * @param {Object} context - Validation context
   * @returns {Promise<Object>} Validation result
   */
  // eslint-disable-next-line no-unused-vars
  async validate(couponCode, context) {
    throw new Error('validate() must be implemented by subclass');
  }

  /**
   * Convert API result to coupon state
   * @abstract
   * @param {Object} apiResult - API response
   * @param {Object} options - Conversion options
   * @returns {CouponState} Converted coupon state
   */
  // eslint-disable-next-line no-unused-vars
  convertApiResultToState(apiResult, options) {
    throw new Error('convertApiResultToState() must be implemented by subclass');
  }

  /**
   * Get discount info for a specific plan
   * @param {string} planCode - Plan code
   * @param {CouponDiscountMapResultBean} discountMap - Discount map
   * @returns {Object|null} Plan discount info or null if not found
   */
  getDiscountInfo(planCode, discountMap) {
    if (!planCode || !discountMap) {
      return null;
    }

    const discountPlans = discountMap?.discountPlans || {};
    return discountPlans[planCode] || null;
  }

  /**
   * Check if this coupon can be applied in current context
   * @param {Object} context - Application context
   * @returns {boolean} Whether coupon can be applied
   */
  // eslint-disable-next-line no-unused-vars
  canApply(context) {
    return true;
  }

  /**
   * Get coupon type identifier
   * @abstract
   * @returns {string} Coupon type constant
   */
  getType() {
    throw new Error('getType() must be implemented by subclass');
  }

  /**
   * Get initial discount result structure
   * @returns {CouponDiscountMapResultBean} Empty discount result
   */
  getInitialResult() {
    return {
      annualDiscount: 0,
      monthlyDiscount: 0,
      quarterlyDiscount: 0,
      twoYearsDiscount: 0,
      threeYearsDiscount: 0,
      annualTermMonthlyDiscount: 0,
      totalDiscount: 0,
      discountPlans: {},
      thresholdResult: {},
    };
  }

  /**
   * Check if a discount value is valid (positive number)
   * @param {number} discount - Discount value to check
   * @returns {boolean} Whether discount is valid
   */
  isValidDiscount(discount) {
    return typeof discount === 'number' && discount > 0 && !isNaN(discount);
  }

  /**
   * Round discount amount to appropriate precision
   * @param {number} amount - Amount to round
   * @param {number} precision - Decimal precision (default: 2)
   * @returns {number} Rounded amount
   */
  roundAmount(amount, precision = 2) {
    const multiplier = Math.pow(10, precision);
    return Math.round(amount * multiplier) / multiplier;
  }

  /**
   * Get strategy name for logging/debugging
   * @returns {string} Strategy name
   */
  getName() {
    return this.constructor.name;
  }
}
